/**
 * StefaniesHandwriting — reusable typography primitives for The Salty
 * Moo™ letter surfaces (Saltie Shout-Out stationery).
 *
 * Per Stefanie's Stationery Typography Revision, ALL handwritten image
 * assets have been retired. The letter is now typeset entirely in the
 * Bakerie brand font for a classic, refined, and enduring look — like
 * a beautifully designed personal letter rather than a handwritten
 * note.
 *
 * Primitives exposed:
 *   • <HandwrittenTitle>    → the permanent letter title, Bakerie
 *   • <HandwrittenBody>     → the multi-paragraph letter body
 *   • <HandwrittenClosing>  → the fixed sign-off: With Love, /
 *                             Stefanie / The Salty Moo™, all Bakerie
 *
 * The component names retain the "Handwritten*" prefix strictly for
 * codebase continuity — the actual rendering is now typeset.
 */
import React from "react";

// Bakerie brand font stack, matching the rest of The Salty Moo™ site.
const BAKERIE = "'Bakerie', 'Cormorant Garamond', 'Garamond', serif";

// The permanent letter title. This phrase is baked into the design and
// is not editable per admin — it appears on every Saltie Shout-Out.
const PERMANENT_TITLE = "Because You Left an Imprint on My Heart";

/**
 * <HandwrittenTitle> — the permanent letter title typeset in Bakerie.
 * Sized as an elegant, understated inscription (not a decorative
 * headline).
 */
export function HandwrittenTitle({ className = "" }) {
  return (
    <h2
      className={`text-center ${className}`}
      style={{
        fontFamily: BAKERIE,
        fontWeight: 400,
        // Personal-inscription scale: quietly authoritative but never
        // shouty. Matches the rest of the brand's Bakerie sizing.
        fontSize: "clamp(1.55rem, 3.2vw, 2.35rem)",
        lineHeight: 1.15,
        letterSpacing: "0.005em",
        color: "#3B2E24",
      }}
      data-testid="handwritten-title"
    >
      {PERMANENT_TITLE}
    </h2>
  );
}

/**
 * <HandwrittenBody> — the editable letter body. Rendered in Bakerie
 * for stationery consistency. Accepts multi-paragraph plain text
 * (blank-line separated).
 */
export function HandwrittenBody({ text, className = "", style = {} }) {
  const paragraphs = (text || "").split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <div
      className={className}
      style={{
        fontFamily: BAKERIE,
        fontWeight: 400,
        // Slightly larger and easier to read.
        fontSize: "clamp(1.13rem, 1.6vw, 1.35rem)",
        // Rich warm charcoal — deeper than #3B2E24 but not pure black,
        // so the letter still reads soft and warm.
        color: "#241C14",
        // Extra line spacing for a peaceful, unhurried cadence.
        lineHeight: 1.85,
        letterSpacing: "0.005em",
        ...style,
      }}
      data-testid="handwritten-body"
    >
      {paragraphs.map((p, i) => (
        <p key={i} style={{ marginTop: i === 0 ? 0 : "0.9em" }}>
          {p.split(/\n/).map((line, j, arr) => (
            <React.Fragment key={j}>
              {line}
              {j < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      ))}
    </div>
  );
}

/**
 * <HandwrittenClosing> — the permanent sign-off, typeset in Bakerie.
 *
 * Hierarchy (per Stefanie's direction): "With Love," is set slightly
 * smaller than her name and the brand, so the graceful cadence of the
 * closing reads as
 *     With Love,      ← lighter, smaller
 *     Stefanie        ← anchor
 *     The Salty Moo™  ← same weight as her name
 */
export function HandwrittenClosing({ className = "" }) {
  const commonStyle = {
    fontFamily: BAKERIE,
    color: "#2C2317",
    letterSpacing: "0.01em",
    lineHeight: 1.15,
  };
  return (
    <div
      className={`flex flex-col ${className}`}
      style={{ alignItems: "flex-start" }}
      data-testid="handwritten-closing"
    >
      <span
        style={{
          ...commonStyle,
          fontWeight: 400,
          fontSize: "clamp(1.05rem, 1.6vw, 1.35rem)",
          marginBottom: "0.35em",
        }}
      >
        With Love,
      </span>
      <span
        style={{
          ...commonStyle,
          fontWeight: 400,
          fontSize: "clamp(1.6rem, 2.6vw, 2.15rem)",
        }}
      >
        Stefanie
      </span>
      <span
        style={{
          ...commonStyle,
          fontWeight: 400,
          fontSize: "clamp(1.5rem, 2.4vw, 1.95rem)",
        }}
      >
        The Salty Moo<sup className="align-super" style={{ fontSize: "0.55em" }}>&trade;</sup>
      </span>
    </div>
  );
}
